'use client';

import { useEffect } from 'react';
import { log } from '@/lib/logger';
import { track } from '@/lib/analytics/client';
import { buildSelectPromotion } from '@/lib/analytics/ecommerce';

/**
 * Reports clicks on promotional calls-to-action as GA4 `select_promotion`
 * (doc 109). Declarative, so any page — hand-written or AI-edited — is
 * measured by adding attributes to a link or button:
 *
 *   data-track-cta="autumn-2026"        required: offer id (its /offers/<slug>)
 *   data-track-cta-slot="hero"          optional: placement on the site
 *   data-track-cta-name="Autumn 2026"   optional: label, defaults to the id
 *
 * One delegated listener on `document`, capture phase, so it sees the click
 * before Next's <Link> handler. It only observes: never preventDefault, never
 * stops propagation, never throws — navigation, anchors and modals are
 * untouched. Consent gating happens in track().
 *
 * Counted: primary-button clicks (incl. ctrl/cmd-click, which open a new tab)
 * and keyboard activation (Enter/Space fire `click`). Not counted: middle-
 * click — it fires `auxclick`, not `click`.
 */
export function CtaClickTracker() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      try {
        if (e.button !== 0 || !(e.target instanceof Element)) return;
        const el = e.target.closest('[data-track-cta]');
        if (!el) return;
        track(
          buildSelectPromotion({
            id: el.getAttribute('data-track-cta'),
            name: el.getAttribute('data-track-cta-name'),
            slot: el.getAttribute('data-track-cta-slot'),
          }),
        );
      } catch (err) {
        log.warn('analytics.cta_click_failed', { err });
      }
    };
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);

  return null;
}
